import { Component, OnInit, OnDestroy } from '@angular/core';
import { MJGlobal, MJEventType } from '@memberjunction/global';
import { LogError, LogStatus, Metadata, RunView } from '@memberjunction/core';
import { GraphQLDataProvider, GraphQLActionClient } from '@memberjunction/graphql-dataprovider';
import { Subscription, filter, take } from 'rxjs';
import { IzzyMetadataEngine } from 'mj_generatedentities';

/**
 * Blocking "complete your profile" modal shown on login when the current user's
 * BAC Person is missing a first or last name.
 *
 * Why this exists: Auth0 database (email/password) signups only collect
 * email + password, so MJ's auth layer has no given_name/family_name to pass to
 * createNewUser. The Person is then created with FirstName falling back to the
 * email-prefix and LastName blank (see IzzyNewUserSubClass). Rather than collect
 * names in the Auth0 hosted signup form (which requires a paid custom domain +
 * Partials), we capture them here, in code we own, on first login.
 *
 * Trigger condition: FirstName empty OR LastName empty. We intentionally do NOT
 * compare FirstName against the email-prefix — a user legitimately named after
 * their email handle (e.g. ian@x.com / "Ian") would false-trigger. The auto
 * fallback always leaves LastName blank, so empty-LastName is the reliable tell.
 */
@Component({
  standalone: false,
  selector: 'izzy-profile-completion',
  templateUrl: './profile-completion.component.html',
  styleUrls: ['./profile-completion.component.css']
})
export class ProfileCompletionComponent implements OnInit, OnDestroy {
  protected visible = false;
  protected firstName = '';
  protected lastName = '';
  protected isSaving = false;
  protected errorMessage = '';

  private loginSub: Subscription | null = null;

  ngOnInit(): void {
    // withReplay=true so this fires even if the user was already logged in
    // before this component subscribed (mirrors the MJ shell's own pattern).
    this.loginSub = MJGlobal.Instance.GetEventListener(true)
      .pipe(filter(e => e.event === MJEventType.LoggedIn), take(1))
      .subscribe(() => {
        void this.checkProfile();
      });
  }

  ngOnDestroy(): void {
    this.loginSub?.unsubscribe();
  }

  private async checkProfile(): Promise<void> {
    try {
      if (this.visible) {
        return;
      }
      await IzzyMetadataEngine.Instance.Config(false);
      const person = IzzyMetadataEngine.Instance.CurrentContact;
      if (!person) {
        // No Person resolved — nothing to complete here; the rest of the app
        // handles the no-person case. Don't block.
        return;
      }

      const first = (person.FirstName ?? '').trim();
      const last = (person.LastName ?? '').trim();
      const email = (person.Email ?? '').trim().toLowerCase();

      // A profile is "incomplete" when a name field is empty OR is just the
      // user's email. We've observed two auto-fallback shapes in the wild:
      //   - IzzyNewUserSubClass: FirstName = email-prefix, LastName = '' (empty)
      //   - MJ auth default:      FirstName = LastName = full email address
      // Matching the FULL email (not the prefix) is unambiguous — nobody's real
      // name is their entire email address — so this avoids the false-positive
      // a prefix-match would cause (e.g. ian@x.com / real name "Ian").
      const looksLikeEmail = (v: string) => v.length > 0 && v.toLowerCase() === email;
      const incomplete = !first || !last || looksLikeEmail(first) || looksLikeEmail(last);

      if (incomplete) {
        // Pre-fill only values that look real (not the email fallback), so the
        // user starts from a clean slate when the existing value is junk.
        this.firstName = looksLikeEmail(first) ? '' : first;
        this.lastName = looksLikeEmail(last) ? '' : last;
        this.visible = true;
      }
    } catch (e) {
      // If the check itself fails, don't trap the user — log and move on.
      LogError(`ProfileCompletion: profile check failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  protected async save(): Promise<void> {
    const first = this.firstName.trim();
    const last = this.lastName.trim();
    if (!first || !last) {
      this.errorMessage = 'Please enter both your first and last name.';
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';
    try {
      // The UI role is read-only on both MJ: Users and MJ_BizApps_Common: People,
      // so the client CANNOT save these directly. Instead we invoke the server
      // action "Update My Profile" (__UpdateMyProfile), which runs server-side
      // under the server DB principal and updates the caller's OWN User + Person
      // (ownership enforced via the action's ContextUser, not a client-supplied ID).
      const provider = Metadata.Provider as GraphQLDataProvider;
      const actionClient = new GraphQLActionClient(provider);

      // Resolve the action ID by name (we don't hardcode IDs — mj sync assigns them).
      const rv = new RunView();
      const actionResult = await rv.RunView<{ ID: string }>({
        EntityName: 'MJ: Actions',
        ExtraFilter: `Name = 'Update My Profile'`,
        ResultType: 'simple'
      });
      const actionId = actionResult.Success && actionResult.Results.length > 0
        ? actionResult.Results[0].ID
        : null;
      if (!actionId) {
        this.errorMessage = 'Profile update is not available. Please contact support.';
        LogError(`ProfileCompletion: "Update My Profile" action not found in metadata`);
        return;
      }

      const result = await actionClient.RunAction(actionId, [
        { Name: 'firstName', Value: first, Type: 'Input' },
        { Name: 'lastName', Value: last, Type: 'Input' },
      ]);

      if (!result?.Success) {
        this.errorMessage = 'Failed to save your profile. Please try again.';
        LogError(`ProfileCompletion: UpdateMyProfile action failed: ${result?.Message ?? 'unknown'}`);
        return;
      }

      LogStatus(`ProfileCompletion: profile updated via action for ${Metadata.Provider.CurrentUser?.Email}`);

      this.visible = false;

      // Full reload so EVERY surface reflects the new name. The DB + Izzy engine
      // are correct immediately, but the MJ shell's cached CurrentUser (which
      // drives the header/avatar) is loaded once at login and isn't refreshable
      // from here. A reload re-bootstraps the app with the fresh user, and the
      // modal won't re-appear (the DB now passes the name-complete check). This
      // is acceptable UX for a one-time, first-login profile-completion step.
      window.location.reload();
    } catch (e) {
      this.errorMessage = 'Failed to save your profile. Please try again.';
      LogError(`ProfileCompletion: save error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      this.isSaving = false;
    }
  }
}

/**
 * Utility function to prevent tree-shaking
 */
export function LoadProfileCompletionComponent(): void {
  // Intentionally empty - prevents tree-shaking
}
